import { Box, Button, CircularProgress, Stack } from "@mui/material";
import { DamsAppAsset } from "../../assets/registry";
import { MainPageBase } from "../page-bases/MainPageBase";
import { useEffect, useState } from "react";
import { DamsEmptyPageProps } from "../page-bases";
import DamsTable from "../../components/Table/CollapsibleTable";
import { NoItemContent } from "../page-bases/NoItemContent";
import { TRowItem } from "../../components/Table/TableRow";
import { AssetMaintenanceApi } from "../../services/maintenance";
import { PageActions, IPagination, IQueryPerams, IAssetMaintenanceSchedule } from "../../config/types";
import { usePageModels } from "../../hooks/usePageActions";
import ActionModal from "../../components/Modals/ActionModal";
import { ModalBase } from "../../components/Modals/ModalBase";
import { ExportModal } from "../../components/Modals/ExportModal";
import { usePagination } from "../../hooks/usePagination";
import { red } from "@mui/material/colors";
import { SimpleLabel } from "../../components/Label/SimpleLabel";
import { useMangeTableItems } from "../../hooks/useManageTableItems";
import { alertDialogStore, actionDialogStore } from "../../store/dialogs";
import appRoutePath from "../../config/path";
import useAppNavigation from "../../hooks/useAppNavigation";
import { MaintenanceScheduleFilter } from "../../common-ui/page-filters/MaintenanceScheduleFilter";
import { formatDate } from "../../utils/common";
import { appConfig } from "../../config/config";

interface FiltersState {
  search?: string;
  asset_category?: string;
  asset_serial_number?: string;
  schedule_status?: string;
  is_deleted: boolean;
}

function MaintenanceMainPage() {
  const [pageState, setPageState] = useState("main");
  const [emptyState, setEmptyState] = useState<DamsEmptyPageProps>();
  const [dataset, setDataset] = useState<TRowItem[]>([]);

  const [pageAction, updatePageAction] = useState<PageActions>();
  const { models, closeModel, openModel } = usePageModels();
  const [isProcessing, setProcess] = useState(false);

  const [currentFilters, setCurrentFilters] = useState<FiltersState>({
    is_deleted: false
  });

  const [querySet, setQuerySet] = useState<IQueryPerams>({ is_deleted: false });

  const [showExportModal, setShowExportModal] = useState(false);
  const [isExportingCSV, setIsExportingCSV] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);

  const {
    pagination,
    paginateToNextPage,
    paginateToPreviousPage,
    paginateToPageNumber,
    updatePaginationInitState,
  } = usePagination<any>(AssetMaintenanceApi.getAssetMaintenanceSchedules);
    
  const {
    selectedRowItems,
    toggleRow,
    toggleSelectAllRow,
    resetSelectedRowItems,
  } = useMangeTableItems(dataset);

  const tkeys = [
    "asset_category",
    "asset_serial_number",
    "schedule_status",
    "service_date",
  ];
  const hcolumns = ["Purpose of Service"];
  const hkeys = ["service_remarks"];

  const columns = [
    "Asset Category",
    "Serial Number",
    "Schedule Status",
    "Maintenance Date",
  ];

  const populateDataSet = (data: IAssetMaintenanceSchedule[], pagination?: IPagination) => {
    const formatData = data.map((data) => ({
      ...data,
      service_date: formatDate(data.service_date),
    })); 
    setDataset(formatData);
    updatePaginationInitState(pagination);
  };

  const fetchData = async (query: IQueryPerams = {}) => {
    try {
      const result = await AssetMaintenanceApi.getAssetMaintenanceSchedules({...query, is_deleted: false});
      if (result?.data) {
        populateDataSet(result.data, result.pagination);
      }
    } catch (err) {
      setEmptyState({ type: "empty-db" });
    }
  };

  useEffect(() => {
    fetchData();
    resetSelectedRowItems();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const page_info = getEmptyPageInfo(pageState);

  const navigate = useAppNavigation();

  function addNewItem() {
    navigate({ path: appRoutePath.ADD_ASSET_MAINTENANCE_PAGE });
  }

  function clearSearch() {
    setPageState("empty-db");
  }

  function openDeleteModel(item: TRowItem) {
    openModel("delete");
    updatePageAction({
      id: item.id,
      name: item.name,
    });
  }

  function handleEdit(item: TRowItem) {
    actionDialogStore.open({
      title: "Edit Maintenance Schedule",
      subtitle: `Are you sure you want to edit this maintenance schedule?`,
      onRightBtnPress: async () =>
        navigate({ path: appRoutePath.EDIT_ASSET_MAINTENANCE_PAGE, params: {id: item.id} }),
      rightBtnTxt: "Continue",
      type: "DELETE",
    });
  }

  async function handleDeleteRequest() {
    setProcess(true);
    const res = await AssetMaintenanceApi.deleteAssetMaintenanceSchedule(
      pageAction?.id ?? ""
    );
    if (res.success) {
      fetchData(querySet);
    }
    setProcess(false);
    resetSelectedRowItems();
    updatePageAction({
      title: res.success
        ? "Delete Schedule Maintenance Record"
        : "Delete Failed",
      message: res.message,
      success: res.success,
    });
    openModel("alert");
  }
  
  function toggleAlertModal() {
    closeModel();
  }

  function openExportModal() {
    setShowExportModal(true);
  }

  function closeExportModal() {
    setShowExportModal(false);
  }

  async function handleExportCSV() {
    setIsExportingCSV(true);
    try {
      const response = await AssetMaintenanceApi.exportAssetMaintenanceSchedules({
        file_format: 'csv',
        ...querySet
      });

      if (response.success && response.data?.download_url) {
        const downloadUrl = response.data.download_url;
        const filename = downloadUrl.split('/').pop();

        const fullDownloadUrl = `${appConfig.baseBackendUrl.replace('/api/v1', '')}${downloadUrl}`;

        try {
          const downloadResponse = await fetch(fullDownloadUrl, {
            method: 'GET'
          });

          if (!downloadResponse.ok) {
            throw new Error('Download failed');
          }

          const blob = await downloadResponse.blob();

          const link = document.createElement('a');
          const url = window.URL.createObjectURL(blob);
          link.href = url;
          link.download = filename || 'maintenance_schedules_export.csv';

          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          updatePageAction({
            title: "Export Successful",
            message: "CSV file has been downloaded successfully.",
            success: true
          });
        } catch (downloadError) {
          window.open(fullDownloadUrl, '_blank');

          updatePageAction({
            title: "Export Initiated",
            message: "CSV export has been initiated. Check your downloads folder.",
            success: true
          });
        }

        openModel("alert");
      } else {
        throw new Error('Invalid response from export API');
      }
    } catch (error) {
      updatePageAction({
        title: "Export Failed",
        message: "CSV export failed. Please try again.",
        success: false
      });
      openModel("alert");
    } finally {
      setIsExportingCSV(false);
      closeExportModal();
    }
  }

  async function handleExportPDF() {
    setIsExportingPDF(true);
    try {
      const response = await AssetMaintenanceApi.exportAssetMaintenanceSchedules({
        file_format: 'pdf',
        ...querySet
      });

      if (response.success && response.data?.download_url) {
        const downloadUrl = response.data.download_url;
        const filename = downloadUrl.split('/').pop();

        const fullDownloadUrl = `${appConfig.baseBackendUrl.replace('/api/v1', '')}${downloadUrl}`;

        try {
          const downloadResponse = await fetch(fullDownloadUrl, {
            method: 'GET'
          });

          if (!downloadResponse.ok) {
            throw new Error('Download failed');
          }

          const blob = await downloadResponse.blob();

          const link = document.createElement('a');
          const url = window.URL.createObjectURL(blob);
          link.href = url;
          link.download = filename || 'maintenance_schedules_export.pdf';

          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          updatePageAction({
            title: "Export Successful",
            message: "PDF file has been downloaded successfully.",
            success: true
          });
        } catch (downloadError) {
          window.open(fullDownloadUrl, '_blank');

          updatePageAction({
            title: "Export Initiated",
            message: "PDF export has been initiated. Check your downloads folder.",
            success: true
          });
        }

        openModel("alert");
      } else {
        throw new Error('Invalid response from export API');
      }
    } catch (error) {
      updatePageAction({
        title: "Export Failed",
        message: "PDF export failed. Please try again.",
        success: false
      });
      openModel("alert");
    } finally {
      setIsExportingPDF(false);
      closeExportModal();
    }
  }
  
  const renderHeaderDropDownMenu = ({
    closeDialog,
  }: {
    closeDialog: () => void;
  }) => {
    const batchDeleteAction = async () => {
      setProcess(true);
      await Promise.all(
        selectedRowItems.map(async(item) => {
           await AssetMaintenanceApi.deleteAssetMaintenanceSchedule(item.id);
        })
      );
      resetSelectedRowItems();
      await fetchData(querySet);
      setProcess(false);
      alertDialogStore.open({
        title: 'Batch Delete',
        subtitle: 'Batch delete successful',
        isSuccess: true 
      });
      closeDialog();
    };
    
    const deleteActionLabel = isProcessing ? '( processing ...)' : `( ${selectedRowItems.length} selected )`;
    
    return (
      <Stack direction={"column"} alignItems={"flex-start"} spacing={1}>
        <Button onClick={batchDeleteAction} variant="text" sx={styles.dialogItemBtn}>
          <SimpleLabel title={`Delete ${deleteActionLabel}`} color={red[700]} fontSize={13}/>
          {isProcessing && <CircularProgress size={12}/>}
        </Button>
      </Stack>
    );
  };
  
  return (
    <MainPageBase
      title={"Maintenance Schedule"}
      subtitle={"View, add, and manage all scheduled maintenance tasks for assets."}
      pageState={pageState}
      addItemBtnOnClick={() => addNewItem()}
      addItemBtnText={"Add Maintenance Schedule"}
      exportBtnOnClick={openExportModal}
      exportBtnText={"Export"}
    >
      <Box
        height={`100%`}
        display={"flex"}
        alignItems={pageState !== "main" ? "center" : ""}
        justifyContent={"center"}
      >
        {pageState !== "main" ? (
          <NoItemContent
            type={pageState}
            title={page_info.title}
            subtitle={page_info.message}
            addItemBtn={() => addNewItem()}
            clearSearchBtn={() => clearSearch()}
          />
        ) : (
          <Stack width={"100%"} spacing={1}>
            <MaintenanceScheduleFilter<IAssetMaintenanceSchedule[]>
              ftn={AssetMaintenanceApi.getAssetMaintenanceSchedules}
              updateDataSet={populateDataSet}
              updateQuerySet={setQuerySet}
            />

            <DamsTable
              tableTitle="Maintenance Schedule History"
              tableSubtitle="Track service dates, status, and asset details for all scheduled maintenance."
              numTableRecords="25"
              tableHeaders={columns}
              hiddenKeys={hkeys}
              hiddenLabels={hcolumns}
              tableKeys={tkeys}
              data={dataset}
              maxRowCells={8}
              onTableItemEdit={handleEdit} 
              onTableItemDelete={openDeleteModel}
              hasPagination
              paginationState={pagination}
              onNextPage={paginateToNextPage(querySet, populateDataSet)}
              onPreviousPage={paginateToPreviousPage(querySet, populateDataSet)}
              setActivePage={paginateToPageNumber(populateDataSet, querySet)}
              headerDropDownMenu={renderHeaderDropDownMenu}
              useTableCheckbox={true}
              onTableItemSelect={toggleRow}
              toggleSelectAllRow={toggleSelectAllRow}
              selectedRowItems={selectedRowItems}
              tableTitleBadge={selectedRowItems.length === 0 ? undefined : selectedRowItems.length + ' selected'}
            />

            {/* Export Modal */}
            <ExportModal
              show={showExportModal}
              onClose={closeExportModal}
              onExportCSV={handleExportCSV}
              onExportPDF={handleExportPDF}
              isExportingCSV={isExportingCSV}
              isExportingPDF={isExportingPDF}
              title="Export Maintenance Schedules"
              subtitle="Choose your preferred format to export maintenance schedule data"
            />
          </Stack>
        )}
      </Box>

      <ModalBase
        open={models.showDeleteModel}
        title={"Delete Schedule Maintenance Record"}
        subtitle={
          <Box>
            Once you delete{" "}
            <span style={{ fontWeight: "bold" }}>{pageAction?.name}</span>
            , ONLY admin can restore. Are you sure you want to proceed?
          </Box>
        }
        type={"action"}
        showHeader={true}
        leftIcon={DamsAppAsset.icons.delete_icon}
        leftBtnTxt="Cancel"
        onLeftBtnPress={closeModel}
        rightBtnTxt="Delete"
        onRightBtnPress={handleDeleteRequest}
        onRightBtnLoading={isProcessing}
        onClose={closeModel}
      />
      
      <ActionModal
        show={models.showAlertModel}
        title={pageAction?.title ?? ""}
        leftIcon={
          pageAction?.success === false
            ? DamsAppAsset.icons.warning
            : undefined
        }
        subtitle={pageAction?.message ?? ""}
        onLeftBtnPress={closeModel}
        leftBtnTxt={"close"}
        onRightBtnPress={toggleAlertModal}
        rightBtnTxt={pageAction?.type === "add" ? "Add New" : "continue"}
        onClosePress={closeModel}
      />
    </MainPageBase>
  );
}

const getEmptyPageInfo = (key: string): { title: string; message: string } => {
  if (page_msg.hasOwnProperty(key)) {
    return page_msg[key];
  } else {
    return { title: "", message: "" };
  }
};

const page_msg: { [key: string]: { title: string; message: string } } = {
  "empty-db": {
    title: "No Maintenance Schedules Found",
    message:
      'No maintenance schedules have been added yet. Click the "Add Maintenance Schedule" button to add a new schedule.'
  },

  "empty-search": {
    title: "No Maintenance Schedules Found",
    message:
      "No maintenance schedules found matching your search criteria. Please try different search terms or add a new maintenance schedule."
  }
};

const styles = {
  dialogItemBtn: {
    textTransform: "none",
    width: "100%",
    padding: 1,
    alignText: "left",
  },
  dialogItemText: {},
};

export default MaintenanceMainPage;